import { defineComponent as _defineComponent } from 'vue';
import { BAlert } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, set as VueSet, unref, watch } from "vue";
import { userOwnsHistory } from "@/api";
import ExpandedItems from "@/components/History/Content/ExpandedItems";
import SelectedItems from "@/components/History/Content/SelectedItems";
import { HistoryFilters } from "@/components/History/HistoryFilters";
import { deleteContent, updateContentFields } from "@/components/History/model/queries";
import { useActiveElement } from "@/composables/useActiveElement";
import { startWatchingHistory } from "@/store/historyStore/model/watchHistory";
import { useHistoryItemsStore } from "@/stores/historyItemsStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import { getOperatorForAlias } from "@/utils/filtering";
import { setItemDragstart } from "@/utils/setDrag";
import { useHistoryDragDrop } from "../../../composables/historyDragDrop";
import HistoryCounter from "./HistoryCounter.vue";
import HistoryDetails from "./HistoryDetails.vue";
import HistoryDropZone from "./HistoryDropZone.vue";
import HistoryEmpty from "./HistoryEmpty.vue";
import HistoryMessages from "./HistoryMessages.vue";
import HistoryOperations from "./HistoryOperations/HistoryOperations.vue";
import OperationErrorDialog from "./HistoryOperations/OperationErrorDialog.vue";
import SelectionChangeWarning from "./HistoryOperations/SelectionChangeWarning.vue";
import HistorySelectionOperations from "./HistoryOperations/SelectionOperations.vue";
import HistorySelectionStatus from "./HistoryOperations/SelectionStatus.vue";
import FilterMenu from "@/components/Common/FilterMenu.vue";
import ContentItem from "@/components/History/Content/ContentItem.vue";
import ListingLayout from "@/components/History/Layout/ListingLayout.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryPanel',
    props: {
        listOffset: { default: 0 },
        history: null,
        filter: { default: "" },
        filterable: { type: Boolean, default: false },
        isMultiViewItem: { type: Boolean, default: false }
    },
    setup(__props) {
        const props = __props;
        const filterClass = HistoryFilters;
        const filterText = ref("");
        const invisibleHistoryItems = ref({});
        const isLoading = ref(false);
        const offsetQueryParam = ref(0);
        const searchError = ref(undefined);
        const showAdvanced = ref(false);
        const operationRunning = ref(null);
        const operationError = ref(null);
        const querySelectionBreak = ref(false);
        const contentItemRefs = computed(() => {
            return historyItems.value.reduce((acc, item) => {
                acc[itemUniqueKey(item)] = ref(null);
                return acc;
            }, {});
        });
        const currItemFocused = useActiveElement();
        const lastItemId = ref(null);
        const { currentFilterText, currentHistoryId, pinnedHistories, storedHistories } = storeToRefs(useHistoryStore());
        const { lastCheckedTime, totalMatchesCount, isWatching } = storeToRefs(useHistoryItemsStore());
        const historyStore = useHistoryStore();
        const historyItemsStore = useHistoryItemsStore();
        const { currentUser } = storeToRefs(useUserStore());
        const historyIdComputed = computed(() => props.history.id);
        const { showDropZone, onDragEnter, onDragLeave, onDragOver, onDrop } = useHistoryDragDrop(historyIdComputed);
        const currentUserOwnsHistory = computed(() => {
            return userOwnsHistory(currentUser.value, props.history);
        });
        const canEditHistory = computed(() => {
            return currentUserOwnsHistory.value && !props.history.deleted && !props.history.archived;
        });
        const historyUpdateTime = computed(() => {
            return props.history.update_time;
        });
        const queryKey = computed(() => {
            return `${props.history.id}-${filterText.value}`;
        });
        const queryDefault = computed(() => {
            return !filterText.value;
        });
        const isProcessing = computed(() => {
            if (!operationRunning.value) {
                return false;
            }
            return operationRunning?.value >= props.history.update_time;
        });
        const historyItems = computed(() => {
            return historyItemsStore.getHistoryItems(props.history.id, filterText.value);
        });
        const visibleHistoryItems = computed(() => {
            return historyItems.value.filter((item) => !invisibleHistoryItems.value[item.hid]);
        });
        const formattedSearchError = computed(() => {
            const newError = unref(searchError);
            if (!newError) {
                return null;
            }
            const { column, col, operation, op, value, val, err_msg, ValueError } = newError;
            const alias = operation || op;
            const operator = alias ? getOperatorForAlias(alias) : ":";
            return {
                filter: `${column || col}${operator}`,
                value: value || val,
                msg: err_msg,
                typeError: ValueError,
            };
        });
        /** Returns a string that indicates whether all the pinned histories have annotations,
         * tags, both, or none of them.
         * This is used to format the `DetailsLayout` header uniformly for multi-view histories.
         */
        const detailsSummarized = computed(() => {
            if (!props.isMultiViewItem) {
                return undefined;
            }
            if (pinnedHistories.value.length === 0) {
                return "hidden";
            }
            let annotation = false;
            let tags = false;
            let loaded = true;
            for (const h of pinnedHistories.value) {
                const history = storedHistories.value[h.id];
                if (!history) {
                    loaded = false;
                    break;
                }
                if (history.annotation) {
                    annotation = true;
                }
                if (history.tags.length > 0) {
                    tags = true;
                }
            }
            if (!loaded || (annotation && tags)) {
                return "both";
            }
            else if (annotation) {
                return "annotation";
            }
            else if (tags) {
                return "tags";
            }
            else {
                return "none";
            }
        });
        const storeFilterText = computed(() => {
            if (props.history.id !== currentHistoryId.value) {
                return "";
            }
            return currentFilterText.value || "";
        });
        const lastItemFocused = computed(() => {
            return lastItemId.value ? historyItems.value.find((item) => lastItemId.value === `dataset-${item.id}`) : null;
        });
        watch(offsetQueryParam, () => loadHistoryItems());
        watch(() => queryKey.value, () => {
            invisibleHistoryItems.value = {};
            offsetQueryParam.value = 0;
            loadHistoryItems();
            lastItemId.value = null;
        });
        watch(() => props.filter, (newVal) => (filterText.value = newVal));
        watch(filterText, (newFilterTextValue) => {
            if (props.filterable) {
                historyStore.setFilterText(props.history.id, newFilterTextValue);
            }
        });
        watch(storeFilterText, (newFilterTextValue) => {
            if (props.filterable) {
                filterText.value = newFilterTextValue;
            }
        });
        watch(historyUpdateTime, async () => await loadHistoryItems());
        watch(() => props.history.id, (newValue, currentValue) => {
            if (newValue !== currentValue) {
                operationRunning.value = null;
                lastItemId.value = null;
            }
        });
        watch(historyItems, (newHistoryItems) => {
            if (!invisibleHistoryItems.value) {
                return;
            }
            // Re-hide invisible history items when `historyItems` changes
            for (const newHistoryItem of newHistoryItems) {
                if (invisibleHistoryItems.value[newHistoryItem.hid]) {
                    VueSet(invisibleHistoryItems.value, newHistoryItem.hid, false);
                }
            }
        });
        watch(() => currItemFocused.value, (newItem, oldItem) => {
            // if user clicked elsewhere, set lastItemId to the last focused item
            // (if it was indeed a history .content-item)
            if (newItem && oldItem?.classList?.contains("content-item") && oldItem?.getAttribute("data-hid")) {
                lastItemId.value = oldItem?.id || null;
            }
        });
        function getHighlight(item) {
            if (unref(isLoading)) {
                return undefined;
            }
            const highlightsKey = parseInt(filterClass.getFilterValue(unref(filterText), "related"));
            if (!highlightsKey) {
                return undefined;
            }
            if (highlightsKey === item.hid) {
                return "active";
            }
            if (item.hid > highlightsKey) {
                return "output";
            }
            return "input";
        }
        function hasMatches(items) {
            return !!items && items.length > 0;
        }
        function isDataset(item) {
            return item.history_content_type === "dataset";
        }
        async function loadHistoryItems() {
            isLoading.value = true;
            try {
                await historyItemsStore.fetchHistoryItems(props.history.id, filterText.value, offsetQueryParam.value);
                searchError.value = undefined;
            }
            catch (error) {
                if (error.response && error.response.data && error.response.data.err_msg) {
                    console.debug("HistoryPanel - Load items error:", error.response.data.err_msg);
                    searchError.value = error.response.data;
                }
                else {
                    console.debug("HistoryPanel - Load items error.", error);
                }
            }
            finally {
                isLoading.value = false;
            }
        }
        async function onDelete(item, recursive = false) {
            isLoading.value = true;
            setInvisible(item);
            try {
                await deleteContent(item, { recursive: recursive });
                updateContentStats();
            }
            finally {
                isLoading.value = false;
            }
        }
        function onHideSelection(selectedItems) {
            for (const item of selectedItems) {
                setInvisible(item);
            }
        }
        function onScroll(newOffset) {
            offsetQueryParam.value = newOffset;
        }
        async function onUndelete(item) {
            setInvisible(item);
            isLoading.value = true;
            try {
                await updateContentFields(item, { deleted: false });
                updateContentStats();
            }
            finally {
                isLoading.value = false;
            }
        }
        async function onUnhide(item) {
            setInvisible(item);
            isLoading.value = true;
            try {
                await updateContentFields(item, { visible: true });
                updateContentStats();
            }
            finally {
                isLoading.value = false;
            }
        }
        function updateContentStats() {
            historyStore.updateContentStats(props.history.id);
        }
        function reloadContents() {
            startWatchingHistory();
        }
        function setInvisible(item) {
            VueSet(unref(invisibleHistoryItems), item.hid, true);
        }
        function onTagChange(item, newTags) {
            item.tags = newTags;
        }
        function onOperationError(error) {
            console.debug("HistoryPanel - Operation error.", error);
            operationError.value = error;
        }
        function updateFilterValue(filterKey, newValue) {
            const currentFilterText = filterText.value;
            filterText.value = filterClass.setFilterValue(currentFilterText, filterKey, newValue);
        }
        function getItemKey(item) {
            return itemUniqueKey(item);
        }
        function itemUniqueKey(item) {
            return `${item.history_content_type}-${item.id}`;
        }
        onMounted(async () => {
            // `filterable` here indicates if this is the current history panel
            if (props.filterable && !props.filter) {
                filterText.value = storeFilterText.value;
            }
            await loadHistoryItems();
            // if there is a listOffset, we are coming from a collection view, so focus on item at that offset
            if (props.listOffset) {
                const itemAtOffset = historyItems.value[props.listOffset];
                if (itemAtOffset) {
                    const itemElement = contentItemRefs.value[itemUniqueKey(itemAtOffset)]?.value?.$el;
                    itemElement?.focus();
                }
            }
        });
        function arrowNavigate(item, eventKey) {
            let nextItem = null;
            if (eventKey === "ArrowDown") {
                nextItem = historyItems.value[historyItems.value.indexOf(item) + 1];
            }
            else if (eventKey === "ArrowUp") {
                nextItem = historyItems.value[historyItems.value.indexOf(item) - 1];
            }
            if (nextItem) {
                const itemElement = contentItemRefs.value[itemUniqueKey(nextItem)]?.value?.$el;
                itemElement?.focus();
            }
            return nextItem;
        }
        return { __sfc: true, props, filterClass, filterText, invisibleHistoryItems, isLoading, offsetQueryParam, searchError, showAdvanced, operationRunning, operationError, querySelectionBreak, contentItemRefs, currItemFocused, lastItemId, currentFilterText, currentHistoryId, pinnedHistories, storedHistories, lastCheckedTime, totalMatchesCount, isWatching, historyStore, historyItemsStore, currentUser, historyIdComputed, showDropZone, onDragEnter, onDragLeave, onDragOver, onDrop, currentUserOwnsHistory, canEditHistory, historyUpdateTime, queryKey, queryDefault, isProcessing, historyItems, visibleHistoryItems, formattedSearchError, detailsSummarized, storeFilterText, lastItemFocused, getHighlight, hasMatches, isDataset, loadHistoryItems, onDelete, onHideSelection, onScroll, onUndelete, onUnhide, updateContentStats, reloadContents, setInvisible, onTagChange, onOperationError, updateFilterValue, getItemKey, itemUniqueKey, arrowNavigate, BAlert, userOwnsHistory, ExpandedItems, SelectedItems, setItemDragstart, HistoryCounter, HistoryDetails, HistoryDropZone, HistoryEmpty, HistoryMessages, HistoryOperations, OperationErrorDialog, SelectionChangeWarning, HistorySelectionOperations, HistorySelectionStatus, FilterMenu, ContentItem, ListingLayout, LoadingSpan };
    }
});
